/**
 * lod0_skins — the LOD0 shape/skin policy (settlement LOD0 research L1,
 * 2026-09-03). Pure; the settlement module and the asset module apply it.
 *
 * A civ building's LOD0 is split in two: the SHAPE (LOD0 geometry with the
 * 512 px textures every other level already shares — the `t512` tier the
 * cooker rewrote from the LOD1 file's bytes) and the SKIN (the mip levels
 * above 512 of each image, a small stand-alone KTX2 per image recorded on
 * the shape's texture `userData.atlasSkin`). The shape is what a LOD1 → LOD0
 * swap loads (geometry only crosses the bus); the skin is fetched only for a
 * key one of whose buildings fills the viewport — the touch band — and is
 * promoted onto the resident texture in place.
 */

/** The cap every LOD0 shape loads at: LOD1's texture resolution. */
export const SHAPE_TEXTURE_CAP = 512;
/** A key's skin is wanted once one of its LOD0 buildings spans this fraction of the viewport height… */
export const SKIN_ON = 0.9;
/** …and released once every one of them has dropped below this (hysteresis). */
export const SKIN_OFF = 0.55;
/** A key keeps its skin this long after it was last wanted (a step back toward the door must not thrash). */
export const SKIN_IDLE_MS = 12_000;
/** Most keys skinned at once (LRU beyond this — VRAM: ~13.5 MB per civ-B key at 1024). */
export const SKIN_MAX_RESIDENT = 12;
/** Skin loads in flight (network + transcode + one GPU copy pass each). */
export const SKIN_LOADS_INFLIGHT = 2;

// ── L6: texel density instead of a fixed threshold ──────────────────────────
/** Textures tile about this many times across a civ facade (the wanted-mip rule divides the building's pixels by it). */
export const SKIN_TILING = 2;
/** Hysteresis on the wanted-resolution rule: a skin is kept until the wanted pixels fall to this fraction of the level's texels. */
export const SKIN_HYST = 0.6;
/** Skins are prefetched (decoded in the background) this many lookaheads out on a closing approach (L8). */
export const SKIN_PREFETCH_LOOKAHEADS = 2;

/** Next power of two ≥ x (x ≥ 1). */
export function pow2ceil(x: number): number { let p = 1; while (p < x) p *= 2; return p; }

/**
 * The texture resolution a building WANTS at one texel per screen pixel: its
 * on-screen height in pixels (screen size × viewport height) divided by the
 * tiling, rounded up to a power of two, clamped to [512, presetCap] — the
 * Unreal/Unity wanted-mip rule with the viewport height as the input, so a
 * 4K screen asks for the skin at half the screen fraction a 1080p one does.
 */
export function wantedTextureRes(screenSize: number, viewportHeightPx: number, presetCap: number, tiling = SKIN_TILING): number {
  const px = Math.max(0, screenSize) * Math.max(1, viewportHeightPx) / Math.max(1, tiling);
  const want = pow2ceil(Math.max(1, px));
  return Math.max(SHAPE_TEXTURE_CAP, Math.min(Math.max(SHAPE_TEXTURE_CAP, presetCap | 0), want));
}

/** The screen-size band the wanted-res rule implies for a viewport: skin ON when the wanted res exceeds the shape's 512, OFF with hysteresis. */
export function skinBandFor(viewportHeightPx: number, tiling = SKIN_TILING, hyst = SKIN_HYST): { on: number; off: number } {
  const H = Math.max(1, viewportHeightPx);
  // pow2ceil(px) > 512 ⇔ px > 512 ⇔ screenSize > 512·tiling/H
  const on = (SHAPE_TEXTURE_CAP * tiling) / H;
  return { on, off: on * hyst };
}

/** Hysteresis on the viewport-derived band (L6) — `skinWanted` keeps the fixed fallback band. */
export function skinWantedAt(screenSize: number, hasSkin: boolean, band: { on: number; off: number }): boolean {
  return hasSkin ? screenSize >= band.off : screenSize >= band.on;
}

/** The texture cap a LOD0 shape loads at under a preset's own cap. */
export function shapeTextureCap(presetMaxTextureRes: number): number {
  return Math.min(SHAPE_TEXTURE_CAP, Math.max(64, presetMaxTextureRes | 0));
}

/** Hysteresis: whether a key's skin is wanted given its largest LOD0 building's screen size (fraction of viewport height). */
export function skinWanted(screenSize: number, hasSkin: boolean): boolean {
  return hasSkin ? screenSize >= SKIN_OFF : screenSize >= SKIN_ON;
}

/** The skin member on a texture for a cap (`userData.atlasSkin.s1024` / `.s2048`), or null when the image has no levels above 512 at that cap. */
export interface SkinRef { uri: string; width: number; height: number; levels: number; bytes: number }
export function skinRefFor(userData: unknown, cap: number): SkinRef | null {
  const ud = userData as { atlasSkin?: Record<string, SkinRef | null | undefined> } | null | undefined;
  const skin = ud?.atlasSkin;
  if (!skin) return null;
  // The largest skin ≤ cap: a 1024 preset takes s1024; ultra takes s2048 when the image has it, else s1024.
  for (const c of [2048, 1024]) {
    if (c > cap) continue;
    const r = skin[`s${c}`];
    if (r && typeof r.uri === 'string') return r;
  }
  return null;
}

/** Which resident keys to demote so at most `max` stay skinned: the least recently wanted first. */
export function skinsToEvict(resident: Array<{ key: string; lastWantedAt: number; wanted: boolean }>, max: number, now: number, idleMs = SKIN_IDLE_MS): string[] {
  const out: string[] = [];
  for (const r of resident) if (!r.wanted && now - r.lastWantedAt >= idleMs) out.push(r.key);
  const keep = resident.filter((r) => !out.includes(r.key));
  if (keep.length > max) {
    keep.sort((a, b) => (a.wanted === b.wanted ? a.lastWantedAt - b.lastWantedAt : a.wanted ? 1 : -1));
    for (const r of keep.slice(0, keep.length - max)) out.push(r.key);
  }
  return out;
}
